import { useQueryClient } from "@tanstack/react-query";
import { GraduationCap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlerts } from "@/shared/api/hooks/alerts";
import { drillTitle, useActiveDrill } from "@/shared/api/hooks/drill";
import { useSites } from "@/shared/api/hooks/sites";
import { qk } from "@/shared/api/keys";
import { useOrgAlertChannel, useSensorChannel } from "@/shared/api/ws";
import { useActiveSiteStore } from "@/shared/state/activeSiteStore";
import { selectUser, useAuth } from "@/shared/auth/store";
import { useTrainingMode } from "@/shared/hooks/useTrainingMode";
import type { Alert, AlertFiredPayload, WsEnvelope } from "@/shared/api/models";
import type { TrainingAlarmEvent } from "@/shared/hooks/useTrainingMode";

/**
 * TrainingModeWatermark — 훈련 모드 전역 오버레이 + 경보 계측기 (기능 #12).
 *
 * AppShell 에 상주. 훈련이 활성일 때만:
 *   * 화면 전체에 대각선 "훈련 중 · TRAINING" 워터마크(입력 비차단)를 깔아
 *     실운영과 시각적으로 구분한다.
 *   * org 경보 WS(`alert.fired`)를 구독해 발생 경보의 시각·등급을 세션에 기록하고
 *     경보 목록도 무효화한다(종료 후 결과 요약의 근거 데이터).
 *
 * 페이지를 이동해도 계측이 끊기지 않도록 라우트 트리 최상단(AppShell)에 둔다.
 */
export function TrainingModeWatermark() {
  const { t } = useTranslation();
  const clientActive = useTrainingMode((s) => s.active);
  const clientName = useTrainingMode((s) => s.scenarioName);
  const recordAlarm = useTrainingMode((s) => s.recordAlarm);
  const qc = useQueryClient();

  const user = useAuth(selectUser);
  const orgId = user?.organization?.id ?? null;

  // 2026-09-08: 훈련의 주체는 서버(drill 세션). 선택 현장의 진행 중 세션을 5초 폴링하고
  // WS `drill.status` 가 오면 즉시 다시 읽는다. 클라이언트 계측 세션(useTrainingMode)은
  // 서버 세션에 맞춰 시작/종료해 경보 타임라인 기록을 이어 간다.
  const sites = useSites({ is_active: true }).data?.results ?? [];
  const pickedSite = useActiveSiteStore((s) => s.siteId);
  const siteId = pickedSite ?? sites[0]?.id ?? null;
  const server = useActiveDrill(siteId).data?.session ?? null;
  useSensorChannel(siteId, (env) => {
    if ((env as { type?: string }).type === "drill.status") {
      qc.invalidateQueries({ queryKey: qk.ops.drill.all });
    }
  });
  const syncedId = useRef<number | null>(null);
  useEffect(() => {
    const st = useTrainingMode.getState();
    if (server && server.status === "running") {
      if (syncedId.current !== server.id) {
        syncedId.current = server.id;
        st.start(`${server.scenario_code} · ${server.scenario_name}`);
      }
    } else if (syncedId.current != null) {
      syncedId.current = null;
      if (st.active) st.stop();
    }
  }, [server]);
  const active = clientActive || (server != null && server.status === "running");
  // 배너에는 짧은 이름만(「S-4 복합 CO+H₂S」). 번역 이름이 이미 코드로 시작하면(「S-4 복합 …」) 코드를 또 붙이지 않고,
  // 「 — 」 뒤 설명(「화재 후 CO 50 ppm + H₂S 8 ppm 동시」)은 떼어 배너가 머리말을 가리지 않게 한다(2026-10-22).
  const scenarioName = server
    ? drillTitle(t, server.scenario_code, server.scenario_name, true)
    : clientName;

  // 훈련 중 새 경보 → 세션 기록 + 목록 무효화.
  useOrgAlertChannel(orgId, (env: WsEnvelope<AlertFiredPayload>) => {
    if (!useTrainingMode.getState().active) return;
    if (env.type === "alert.fired") {
      const p = env.payload;
      recordAlarm({
        alertId: p?.id ?? null,
        level: p?.level ?? 0,
        gas: p?.gas ?? "",
        siteId: p?.site ?? env.site_id ?? null,
        at: Date.now(),
      });
    }
    if (env.type === "alert.fired" || env.type === "alert.cleared") {
      qc.invalidateQueries({ queryKey: qk.safety.alerts.all });
    }
  });

  // WS 미연결(폴백) 환경 대비: 훈련 중에는 경보 목록을 주기 폴링해 새 경보를 흡수.
  const { data: alertsData } = useAlerts();
  useTrainingAlarmPolling(active, alertsData?.results, recordAlarm);

  if (!active) return null;

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[60] overflow-hidden"
      style={{ animation: "fade-in 200ms ease both" }}
    >
      {/* 대각선 반복 워터마크 */}
      <div
        className="absolute inset-[-20%] opacity-[0.06]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(-45deg, var(--warning,#f59e0b) 0 2px, transparent 2px 220px)",
        }}
      />
      {/* 상단 훈련 배지 */}
      {/* fixed inset-0 안의 top-3 이라 배지가 y=12px — 다이내믹 아일랜드(≈59px)에
          완전히 파묻혔다. 훈련 중임을 알리는 표식이 안 보이면 실제 경보와
          구분이 안 되므로 안전영역만큼 내린다. */}
      <div
        className="absolute left-1/2 -translate-x-1/2"
        style={{ top: "calc(var(--safe-top) + 0.75rem)" }}
      >
        <div
          className="flex items-center gap-2 rounded-full px-4 py-1.5 text-[13px] font-bold text-white shadow-lg"
          style={{ background: "var(--warning, #f59e0b)" }}
        >
          <GraduationCap size={16} strokeWidth={2.25} />
          {t("widgets.trainingWatermark.badge")}
          {scenarioName && <span className="font-medium opacity-90">· {scenarioName}</span>}
        </div>
      </div>
      {/* 하단 큰 워터마크 문구 */}
      <div
        className="absolute bottom-6 right-6 select-none text-[13px] font-semibold uppercase tracking-[0.2em]"
        style={{ color: "var(--warning, #f59e0b)", opacity: 0.5 }}
      >
        {t("widgets.trainingWatermark.notice")}
      </div>
    </div>
  );
}

/** 훈련 중, 경보 목록이 폴링으로 갱신되면 신규 경보를 세션에 흡수(WS 폴백). */
function useTrainingAlarmPolling(
  active: boolean,
  alerts: Alert[] | undefined,
  recordAlarm: (e: TrainingAlarmEvent) => void,
) {
  const [seenAt] = useState(() => Date.now());
  useEffect(() => {
    if (!active || !alerts) return;
    for (const a of alerts) {
      // 훈련 시작 이후 발생한 것만.
      const raised = a.raised_at ? new Date(a.raised_at).getTime() : 0;
      const start = useTrainingMode.getState().startedAt ?? seenAt;
      if (raised >= start) {
        recordAlarm({
          alertId: a.id ?? null,
          level: a.level ?? 0,
          gas: a.gas ?? "",
          siteId: a.site ?? null,
          at: raised || Date.now(),
        });
      }
    }
  }, [active, alerts, recordAlarm, seenAt]);
}
